import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn'

/* ------------------------------------------------------------------
   Toast — سند مادر بند ۱۱.۱
   اعلان لحظه‌ای رابط. با Notification Service هسته (بند ۱۲.۲)
   اشتباه گرفته نشود؛ آن یکی ماندگار و چندکاناله است.
   ------------------------------------------------------------------ */

export type ToastTone = 'info' | 'success' | 'warning' | 'danger'

export interface ToastOptions {
  title: string
  description?: ReactNode
  tone?: ToastTone
  /** میلی‌ثانیه — صفر یعنی تا بسته‌شدن دستی بماند */
  duration?: number
  action?: { label: string; onClick: () => void }
}

interface ToastItem extends ToastOptions {
  id: number
}

interface ToastContextValue {
  toast: (options: ToastOptions) => number
  dismiss: (id: number) => void
  success: (title: string, description?: ReactNode) => number
  error: (title: string, description?: ReactNode) => number
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast باید داخل <ToastProvider> استفاده شود')
  return context
}

const TONES: Record<ToastTone, { bar: string; icon: ReactNode }> = {
  info: {
    bar: 'bg-[var(--pf-info-500)]',
    icon: (
      <path
        d="M10 9v5m0-8.5h.01"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    ),
  },
  success: {
    bar: 'bg-[var(--pf-success-500)]',
    icon: (
      <path
        d="m6 10.5 2.5 2.5L14 7.5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ),
  },
  warning: {
    bar: 'bg-[var(--pf-warning-500)]',
    icon: (
      <path d="M10 7v4m0 2.5h.01" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    ),
  },
  danger: {
    bar: 'bg-[var(--pf-danger-500)]',
    icon: (
      <path
        d="m7 7 6 6M13 7l-6 6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    ),
  },
}

const TONE_TEXT: Record<ToastTone, string> = {
  info: 'text-[var(--pf-info-700)]',
  success: 'text-[var(--pf-success-700)]',
  warning: 'text-[var(--pf-warning-700)]',
  danger: 'text-[var(--pf-danger-700)]',
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const nextId = useRef(1)
  const timers = useRef(new Map<number, number>())

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id))
    const timer = timers.current.get(id)
    if (timer) {
      window.clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current++
      const duration = options.duration ?? (options.tone === 'danger' ? 8000 : 4500)

      setItems((current) => [...current, { ...options, id }])

      if (duration > 0) {
        timers.current.set(id, window.setTimeout(() => dismiss(id), duration))
      }
      return id
    },
    [dismiss],
  )

  const value = useMemo<ToastContextValue>(
    () => ({
      toast,
      dismiss,
      success: (title, description) => toast({ title, description, tone: 'success' }),
      error: (title, description) => toast({ title, description, tone: 'danger' }),
    }),
    [toast, dismiss],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}

      {createPortal(
        <div
          role="region"
          aria-label="اعلان‌ها"
          className="pointer-events-none fixed bottom-4 start-4 flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
          style={{ zIndex: 'var(--pf-z-toast)' }}
        >
          {items.map((item) => {
            const tone = item.tone ?? 'info'
            return (
              <div
                key={item.id}
                role={tone === 'danger' ? 'alert' : 'status'}
                aria-live={tone === 'danger' ? 'assertive' : 'polite'}
                className={cn(
                  'pointer-events-auto flex gap-3 overflow-hidden rounded-[var(--pf-radius-md)]',
                  'border border-[var(--pf-border)] bg-[var(--pf-surface)] shadow-[var(--pf-shadow-lg)]',
                  'animate-[pf-toast-in_180ms_cubic-bezier(0.4,0,0.2,1)]',
                )}
              >
                <span className={cn('w-1 shrink-0', TONES[tone].bar)} aria-hidden="true" />

                <div className="flex min-w-0 flex-1 items-start gap-2.5 py-3 pe-3">
                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                    className={cn('mt-0.5 size-4 shrink-0', TONE_TEXT[tone])}
                    aria-hidden="true"
                  >
                    {TONES[tone].icon}
                  </svg>

                  <div className="min-w-0 flex-1">
                    <p className="text-[length:var(--pf-text-sm)] font-[var(--pf-weight-medium)] text-[var(--pf-text)]">
                      {item.title}
                    </p>
                    {item.description && (
                      <p className="mt-0.5 text-[length:var(--pf-text-xs)] text-[var(--pf-text-muted)]">
                        {item.description}
                      </p>
                    )}
                    {item.action && (
                      <button
                        type="button"
                        onClick={() => {
                          item.action!.onClick()
                          dismiss(item.id)
                        }}
                        className="mt-1.5 text-[length:var(--pf-text-xs)] font-[var(--pf-weight-medium)] text-[var(--pf-link)] hover:underline"
                      >
                        {item.action.label}
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => dismiss(item.id)}
                    aria-label="بستن اعلان"
                    className="flex size-6 shrink-0 items-center justify-center rounded-[var(--pf-radius-sm)] text-[var(--pf-text-subtle)] transition-colors hover:bg-[var(--pf-surface-hover)] hover:text-[var(--pf-text)]"
                  >
                    <svg viewBox="0 0 20 20" fill="none" className="size-3.5" aria-hidden="true">
                      <path
                        d="m5 5 10 10M15 5 5 15"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}
